import { InfoPage } from '@/components/info-page';
import { PageBreadcrumbProps } from '@/components/page-breadcrumb';
import { EntityTable } from '@/components/table/entity-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown';
import { TableFilterInputField } from '@/components/table/table-filter-field';
import { TableFilterPlainDatePicker } from '@/components/table/table-filter-plain-date-picker';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';
import { CustomDialog } from '@/components/ui/custom-dialog';
import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu';
import { useClassTimeOptions } from '@/lib/api-call-hooks';
import { EmptyArray, getSchool, getStudentGroups } from '@/lib/api-calls';
import { useRowsBulkSelection } from '@/lib/bulk-selection';
import { useErrorToast } from '@/lib/error-handling';
import { usePaginatedFilters } from '@/lib/filtering';
import { createFormOptions } from '@/lib/form-utils';
import { paginationRequestSchema } from '@/lib/pagination';
import { useNavigateSearchState, validateRouteRole } from '@/lib/routing';
import { useSorting } from '@/lib/sorting';
import { BulkUpdateClasses } from '@/routes/_layout/orgs/schools/-components/bulk-update-classes';
import { getOneTimeClassTableColumns } from '@/routes/_layout/orgs/schools/-components/school-class-table-columns';
import { GenerateSessionsForOneTimeClasses } from '@/routes/_layout/scheduling/student-groups/-components/generate-sessions-for-one-time-classes';
import { FrequentTimeZones, isAirAdmin, plainDateFromStringOrElseUndefined } from '@airreading/common';
import {
  EditStudentGroupBulkActions,
  GetStudentGroupsSortableColumn,
  getStudentGroupsSortOrderSchema,
  StudentGroupStatus,
  StudentGroupStatusLabelMap,
  studentGroupStatusSchema,
  StudentGroupWithScheduleAndLevel,
} from '@airreading/common/data';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';
import { schoolDetailsTabs } from '../../-components/tabs';

export const Route = createFileRoute('/_layout/orgs/schools/$schoolId/one-time-classes/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: SchoolOneTimeClassesDetails,
  validateSearch: paginationRequestSchema.extend({
    query: z.string().optional(),
    scheduleSchoolId: z.array(z.string()).optional(),
    statuses: z.array(z.nativeEnum(StudentGroupStatus)).optional(),
    classTimeIds: z.array(z.string()).optional(),
    sortBy: getStudentGroupsSortOrderSchema.optional(),
    overrideStartPlainDate: z.string().optional(),
  }),
});

const allStatuesFilterOptions = createFormOptions<StudentGroupStatus>(
  studentGroupStatusSchema.options,
  StudentGroupStatusLabelMap,
);

type OneTimeBulkUpdateMenuItemProps = {
  hasFutureSessions: boolean;
  hasAnyPastDay: boolean;
  disabled?: boolean;
  studentGroups: StudentGroupWithScheduleAndLevel[];
  schoolId: string;
  timeZone: string | null;
  onSuccess: () => void;
  bulkType:
    | typeof EditStudentGroupBulkActions.UpdateTeacher
    | typeof EditStudentGroupBulkActions.UpdateStudents
    | typeof EditStudentGroupBulkActions.UpdateLesson;
  modalTitle: string;
  menuLabel: string;
  onNoActiveSession: () => void;
};

function OneTimeBulkUpdateMenuItem({
  hasFutureSessions,
  hasAnyPastDay,
  disabled = false,
  studentGroups,
  schoolId,
  timeZone,
  onSuccess,
  bulkType,
  modalTitle,
  menuLabel,
  onNoActiveSession,
}: OneTimeBulkUpdateMenuItemProps) {
  const isMenuDisabled = hasAnyPastDay || disabled;
  if (hasFutureSessions) {
    return (
      <BulkUpdateClasses
        studentGroups={studentGroups}
        trigger={
          <DropdownMenuItem onSelect={(e) => e.preventDefault()} disabled={isMenuDisabled}>
            {menuLabel}
          </DropdownMenuItem>
        }
        bulkType={bulkType}
        title={modalTitle}
        schoolId={schoolId}
        timeZone={timeZone}
        onSuccess={onSuccess}
        isOneTimeClass
      />
    );
  }

  return (
    <DropdownMenuItem
      onSelect={(e) => {
        e.preventDefault();
        onNoActiveSession();
      }}
      disabled={isMenuDisabled}
    >
      {menuLabel}
    </DropdownMenuItem>
  );
}

function SchoolOneTimeClassesDetails() {
  const { schoolId } = Route.useParams();
  const { data } = useSuspenseQuery(getSchool(schoolId));
  const timeZone = data.org.timeZone;

  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[nameQuery, activeNameQuery], setNameQuery],
      statuses: [[selectedStatuses, activeSelectedStatuses], setSelectedStatuses],
      classTimeIds: [[classTimeIds, activeClassTimeIds], setClassTimeIds],
      overrideStartPlainDate: [[overrideStartPlainDate, activeOverrideStartPlainDate], setOverrideStartPlainDate],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query', 'statuses', 'classTimeIds', 'overrideStartPlainDate']);
  const [sortBy, didChangeSortBy] = useNavigateSearchState(Route.id, 'sortBy');
  const { sorting, ...tableSortingOptions } = useSorting<GetStudentGroupsSortableColumn>([sortBy, didChangeSortBy]);

  const { classTimeOptions } = useClassTimeOptions({ schoolId });

  const {
    data: studentGroupsResponse,
    error,
    refetch,
    isPending,
  } = useQuery(
    getStudentGroups({
      pageIndex,
      pageSize,
      filters: {
        query: activeNameQuery,
        scheduleSchools: [data.id],
        statuses: activeSelectedStatuses,
        classTimeIds: activeClassTimeIds,
        classTimeTypes: ['one_time'],
        overrideStartPlainDate: plainDateFromStringOrElseUndefined(activeOverrideStartPlainDate),
      },
      sortBy,
    }),
  );
  const studentGroups = useMemo(() => studentGroupsResponse?.studentGroups || EmptyArray, [studentGroupsResponse]);
  useErrorToast(error, 'Error loading student groups. Please try again.');

  const { rowSelection, rowDataSelection, onRowSelectionChange, getRowId, resetAllRowSelection } =
    useRowsBulkSelection<StudentGroupWithScheduleAndLevel>({
      getRowId: (row) => row.id,
      rowIdToData: (id) => studentGroups.find((studentGroup) => studentGroup.id === id),
    });

  const columns = useMemo(
    () => getOneTimeClassTableColumns(schoolId, data.org.timeZone),
    [schoolId, data.org.timeZone],
  );
  const table = useReactTable({
    data: studentGroups ?? EmptyArray,
    columns,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
      sorting,
      rowSelection,
    },
    rowCount: studentGroupsResponse?.total || 0,
    getCoreRowModel: getCoreRowModel(),
    getRowId,
    onPaginationChange,
    onRowSelectionChange,
    manualPagination: true,
    ...tableSortingOptions,
  });

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          placeholder="Search by students, teachers, class times, etc."
          value={nameQuery ?? ''}
          onChange={(event) => {
            setNameQuery(event.target.value);
          }}
        />
        <TableFilterPlainDatePicker
          onPlainDateSelect={setOverrideStartPlainDate}
          selectedPlainDateString={overrideStartPlainDate}
          onClear={() => setOverrideStartPlainDate(undefined)}
          tableFilterDatePickerProps={{
            placeholder: 'Filter by one-time class date',
          }}
        />
        <TableFilterDropdown
          title="Class time (CT)"
          options={classTimeOptions}
          selectedValues={classTimeIds ?? []}
          onChange={setClassTimeIds}
        />
        <TableFilterDropdown
          title="Status"
          options={allStatuesFilterOptions}
          onChange={setSelectedStatuses}
          selectedValues={selectedStatuses ?? []}
        />
        <TableFilterResetButton show onClick={resetFilters} />
      </>
    ),
    [
      nameQuery,
      setOverrideStartPlainDate,
      overrideStartPlainDate,
      classTimeOptions,
      classTimeIds,
      setClassTimeIds,
      setSelectedStatuses,
      selectedStatuses,
      resetFilters,
      setNameQuery,
    ],
  );

  const breadcrumbs: PageBreadcrumbProps = useMemo(() => {
    return {
      linkItems: [
        {
          label: 'Schools',
          link: {
            to: '/orgs/schools',
          },
        },
      ],
      currentPage: data.name,
    };
  }, [data]);

  const hasFutureSessions = rowDataSelection.some((sg) => (sg.futureSessions ?? 0) > 0);
  const allSelectedHaveFutureSessions =
    rowDataSelection.length > 0 && rowDataSelection.every((sg) => (sg.futureSessions ?? 0) > 0);
  const hasMixedFutureSessions = rowDataSelection.length > 1 && hasFutureSessions && !allSelectedHaveFutureSessions;

  const [noActiveSessionDialogOpen, setNoActiveSessionDialogOpen] = useState(false);

  useEffect(() => {
    if (hasFutureSessions) {
      setNoActiveSessionDialogOpen(false);
    }
  }, [hasFutureSessions]);

  const todayInCST = Temporal.Now.zonedDateTimeISO(FrequentTimeZones.UsCentral).toPlainDate();
  const hasAnyPastDay = rowDataSelection.some((sg) => {
    const classDate = sg.overrideStartPlainDate || sg.classTime.schedule.startPlainDate;
    return Temporal.PlainDate.compare(classDate, todayInCST) < 0;
  });

  const bulkActions = useMemo(() => {
    return (
      <>
        <GenerateSessionsForOneTimeClasses
          trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Generate new sessions</DropdownMenuItem>}
          studentGroups={rowDataSelection}
          onSuccess={resetAllRowSelection}
        />
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>Update sessions</DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <OneTimeBulkUpdateMenuItem
              hasFutureSessions={hasFutureSessions}
              hasAnyPastDay={hasAnyPastDay}
              disabled={rowDataSelection.length > 1}
              studentGroups={rowDataSelection}
              schoolId={schoolId}
              timeZone={timeZone}
              onSuccess={resetAllRowSelection}
              bulkType={EditStudentGroupBulkActions.UpdateTeacher}
              modalTitle="Update teacher"
              menuLabel="Update teacher"
              onNoActiveSession={() => setNoActiveSessionDialogOpen(true)}
            />
            <OneTimeBulkUpdateMenuItem
              hasFutureSessions={hasFutureSessions}
              hasAnyPastDay={hasAnyPastDay}
              studentGroups={rowDataSelection}
              schoolId={schoolId}
              disabled={rowDataSelection.length > 1}
              timeZone={timeZone}
              onSuccess={resetAllRowSelection}
              bulkType={EditStudentGroupBulkActions.UpdateStudents}
              modalTitle="Update students"
              menuLabel="Update students"
              onNoActiveSession={() => setNoActiveSessionDialogOpen(true)}
            />
            <OneTimeBulkUpdateMenuItem
              hasFutureSessions={allSelectedHaveFutureSessions}
              hasAnyPastDay={hasAnyPastDay}
              disabled={hasMixedFutureSessions}
              studentGroups={rowDataSelection}
              schoolId={schoolId}
              timeZone={timeZone}
              onSuccess={resetAllRowSelection}
              bulkType={EditStudentGroupBulkActions.UpdateLesson}
              modalTitle="Update lesson"
              menuLabel="Update lessons"
              onNoActiveSession={() => setNoActiveSessionDialogOpen(true)}
            />
            <BulkUpdateClasses
              studentGroups={rowDataSelection}
              trigger={
                <DropdownMenuItem onSelect={(e) => e.preventDefault()} disabled={hasAnyPastDay}>
                  Cancel future sessions
                </DropdownMenuItem>
              }
              bulkType={EditStudentGroupBulkActions.CancelOneTimeClassFutureSessions}
              title="Bulk cancel future sessions"
              schoolId={schoolId}
              timeZone={timeZone}
              onSuccess={resetAllRowSelection}
              isOneTimeClass
            />
            <BulkUpdateClasses
              studentGroups={rowDataSelection}
              trigger={
                <DropdownMenuItem onSelect={(e) => e.preventDefault()} disabled={hasAnyPastDay}>
                  Recreate sessions
                </DropdownMenuItem>
              }
              bulkType={EditStudentGroupBulkActions.RecreateSessionsForOneTimeClass}
              title="Bulk recreate sessions"
              schoolId={schoolId}
              timeZone={timeZone}
              onSuccess={resetAllRowSelection}
              isOneTimeClass
            />
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <BulkUpdateClasses
          studentGroups={rowDataSelection}
          trigger={
            <DropdownMenuItem onSelect={(e) => e.preventDefault()} disabled={hasFutureSessions}>
              Archive classes
            </DropdownMenuItem>
          }
          bulkType={EditStudentGroupBulkActions.ArchiveSessions}
          title="Bulk Archive Classes"
          schoolId={schoolId}
          timeZone={timeZone}
          onSuccess={resetAllRowSelection}
          isOneTimeClass
        />
      </>
    );
  }, [
    rowDataSelection,
    resetAllRowSelection,
    hasFutureSessions,
    allSelectedHaveFutureSessions,
    hasMixedFutureSessions,
    hasAnyPastDay,
    schoolId,
    timeZone,
  ]);

  return (
    <InfoPage headerText={data.name || data.id} breadcrumbs={breadcrumbs} tabs={schoolDetailsTabs({ schoolId })}>
      {!hasFutureSessions && (
        <CustomDialog
          title="No active session to update"
          text="There are no active sessions to update. Please select a one-time class with future sessions."
          open={noActiveSessionDialogOpen}
          setOpen={setNoActiveSessionDialogOpen}
          closeButtonText="Close"
        />
      )}
      <EntityTable
        table={table}
        isLoading={isPending}
        showPagination
        emptyState={'No one-time classes found'}
        hasError={!!error}
        errorState={<TableErrorState refetchAction={refetch} />}
        bulkActionsMenuItems={bulkActions}
        filters={{ filterLeftComponents }}
      />
    </InfoPage>
  );
}
